import { useEffect, useRef, useState } from "react";

interface HeroImageProps {
  src: string;
  alt?: string;
  className?: string;
}

export function HeroImage({ src, alt = "", className = "" }: HeroImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [offset, setOffset] = useState(0);
  const rafRef = useRef<number | null>(null);
  const containerTopRef = useRef(0);
  const containerHeightRef = useRef(0);

  useEffect(() => {
    const recalcBounds = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      containerTopRef.current = rect.top + window.scrollY;
      containerHeightRef.current = rect.height;
    };

    recalcBounds();
    window.addEventListener("resize", recalcBounds, { passive: true });

    const handleScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const scrollY = window.scrollY;
        const top = containerTopRef.current - scrollY;
        const windowH = window.innerHeight;
        const height = containerHeightRef.current;
        if (top + height < 0 || top > windowH) return;
        const progress = (windowH - top) / (windowH + height);
        setOffset(progress * 200 - 100);
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", recalcBounds);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden">
      <img
        src={src}
        alt={alt}
        className={`w-full h-[140%] object-cover transition-opacity duration-1000 ease-out ${loaded ? "opacity-100" : "opacity-0"} ${className}`}
        style={{ transform: `translate3d(0, ${offset}px, 0)`, willChange: "transform" }}
        loading="eager"
        decoding="async"
        {...{ fetchpriority: "high" } as any}
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
}
